import { useContext, useEffect, useLayoutEffect, useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage, FormattedPlural } from "react-intl";
import {
	autoUpdate,
	FloatingPortal,
	useClick,
	useDismiss,
	useFloating,
	useInteractions,
	useTransitionStyles,
} from "@floating-ui/react";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import "./LastSearch.scss";
import classNames from "classnames";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import AppGlobalsContext from "app/AppGlobalsContext";
import LastSearchDrawer from "app/features/SearchEngine/LastSearch/components/LastSearchDrawer/LastSearchDrawer";
import LastSearchList from "app/features/SearchEngine/LastSearch/components/LastSearchList/LastSearchList";
import SeparatorLine from "app/components/ui/Separator/SeparatorLine";
import { WEBSITE_TUNNEL_TYPES } from "app/constants";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { ArrowRightIcon } from "app/icons/navigation";
import Button, {
	BUTTON_ICON_ALIGNMENTS,
	BUTTON_SIZES,
	BUTTON_VARIANTS,
} from "app/pages/.shared/form/Button";

const popperOffset = [0, -30];

const LastSearch = ({
	updateFloatingPositionReference = () => {},
	websiteType,
	stopoverCities = [],
	lastSearches = [],
	onClearHistory,
	onRemoveSearch,
	onItemClick = () => {},
	onFocus = () => {},
	isCompact = false,
}) => {
	const { resolution } = useContext(AppGlobalsContext);
	const isMobile = resolution === RESOLUTION.MEDIUM || resolution === RESOLUTION.SMALL;
	const lastSearchCount = lastSearches?.length;
	const { track } = useAnalytics();

	const [showLastSearches, setShowLastSearches] = useState(false);
	const [showLastSearchesDrawer, setShowLastSearchesDrawer] = useState(false);

	const { context, x, y, strategy, refs } = useFloating({
		placement: "bottom",
		whileElementsMounted: autoUpdate,
		open: showLastSearches,
		onOpenChange: open => {
			setShowLastSearches(open);

			if (open) {
				track("last-searches-button-click-open", {
					websiteType,
				});
			}
		},
	});

	const dismiss = useDismiss(context, { ancestorScroll: false });
	const click = useClick(context, {
		event: "mousedown",
		toggle: true,
	});

	const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

	const { styles: transitionStyles, isMounted } = useTransitionStyles(context, {
		duration: 250,
		initial: {
			opacity: 0,
			transform: "translateY(0px)",
		},
		open: {
			opacity: 1,
			transform: "translateY(20px)",
		},
		close: {
			opacity: 0,
			transform: "translateY(0px)",
		},
	});

	useLayoutEffect(() => {
		// need to call this hook if showLastSearches changes to trigger the update of the floating position
		if (showLastSearches) {
			updateFloatingPositionReference(refs);
		}
	}, [showLastSearches]);

	const handleSearchItemClick = id => {
		onItemClick(id);
		if (isMobile) {
			setShowLastSearchesDrawer(false);
		} else {
			setShowLastSearches(false);
		}
		track("last-searches-item-click", {
			websiteType,
		});
	};

	const buttonClassName = classNames("last-search__button", {
		"last-search__button--active": showLastSearches,
	});

	const computedSearchEntry = searchEntry => {
		const data = searchEntry?.data || {};

		const adults = data?.adults ? Number(data.adults) : 0;
		const infants = data?.infants ? Number(data.infants) : 0;
		const children = data?.children ? Number(data.children) : 0;

		const stopoverCityLabel = stopoverCities?.find(
			city => city.code === searchEntry?.data?.stopoverCityId
		)?.label;

		return {
			departureCity: data?.departureCity?.label?.replace(/\s+\([A-Z]{3}\)$/, ""),
			destination: data?.searchedAccommodation?.name || data?.destinationResort?.label,
			departureDate: data?.departureDate,
			stopoverCityLabel: stopoverCityLabel,
			stopoverCityId: data?.stopoverCityId,
			stopoverDirection: data?.stopoverDirection,
			endDate: data?.endDate,
			cabin: data?.cabin?.label,
			passengerCount: adults + infants + children,
			roomCount: Number(data?.occupancies?.length),
			id: searchEntry.id,
		};
	};

	const lastSearchesFormatted = lastSearches?.map(computedSearchEntry);

	const listClassName = classNames("last-search__list", {
		"last-search__list--holidays": !isCompact && websiteType === WEBSITE_TUNNEL_TYPES.HOLIDAYS,
		"last-search__list--stopover": !isCompact && websiteType === WEBSITE_TUNNEL_TYPES.STOPOVER,
		"last-search__list--hotel-only":
			!isCompact && websiteType === WEBSITE_TUNNEL_TYPES.HOTEL_ONLY,
		"last-search__list--compact": isCompact,
	});

	useEffect(() => {
		if (showLastSearches) {
			onFocus();
		}
	}, [showLastSearches]);

	return (
		<>
			{lastSearchCount > 0 && (
				<Button
					variant={BUTTON_VARIANTS.BORDERLESS}
					size={BUTTON_SIZES.DEFAULT}
					className={buttonClassName}
					data-testid="last-search-open-button"
					onClick={() => {
						if (isMobile) {
							setShowLastSearchesDrawer(true);
						}
						if (!showLastSearches) {
							track("last-searches-button-click-open", {
								websiteType,
							});
						}
					}}
					{...(!isMobile && {
						ref: refs.setReference,
						...getReferenceProps(),
					})}
					label={
						<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR} isBold>
							<FormattedPlural
								value={lastSearchCount}
								one={
									<FormattedMessage
										id="sdp.search.last.searches.button.label.singular"
										values={{
											count: lastSearchCount,
										}}
									/>
								}
								other={
									<FormattedMessage
										id="sdp.search.last.searches.button.label.plural"
										values={{
											count: lastSearchCount,
										}}
									/>
								}
							/>
						</Typography>
					}
					icon={<ArrowRightIcon />}
					alignIcon={BUTTON_ICON_ALIGNMENTS.RIGHT}
				/>
			)}
			{!isMobile && lastSearchCount > 0 && (
				<FloatingPortal preserveTabOrder={false}>
					{isMounted && (
						<div
							className={listClassName}
							ref={refs.setFloating}
							{...getFloatingProps()}
							style={{
								position: strategy,
								left: x + (popperOffset[0] ?? 0),
								top: y + (popperOffset[1] ?? 0) + (isCompact ? 16 : 0),
								width: isCompact ? undefined : 1024,
								...transitionStyles,
							}}
						>
							<LastSearchList
								items={lastSearchesFormatted}
								onClearHistory={onClearHistory}
								onRemoveSearch={onRemoveSearch}
								onItemClick={handleSearchItemClick}
								isCompact={isCompact}
								separator={isCompact ? undefined : <SeparatorLine vertical />}
							/>
							{lastSearchCount >= 1 && (
								<Button
									variant={BUTTON_VARIANTS.BORDERLESS}
									size={BUTTON_SIZES.SMALL}
									label={
										<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR} isBold>
											<FormattedMessage id="sdp.search.last.searches.clear.button.label" />
										</Typography>
									}
									data-testid="last-search-clear-button"
									onClick={onClearHistory}
									className="last-search__clear-button"
								/>
							)}
						</div>
					)}
				</FloatingPortal>
			)}

			{isMobile && (
				<LastSearchDrawer
					lastSearches={lastSearchesFormatted}
					isOpen={showLastSearchesDrawer}
					onClearHistory={onClearHistory}
					onRemoveSearch={onRemoveSearch}
					onItemClick={handleSearchItemClick}
					onClose={() => setShowLastSearchesDrawer(false)}
				/>
			)}
		</>
	);
};

LastSearch.propTypes = {
	onRemoveSearch: PropTypes.func,
	onClearHistory: PropTypes.func,
	websiteType: PropTypes.oneOf(Object.values(WEBSITE_TUNNEL_TYPES)),
	stopoverCities: PropTypes.array,
	onItemClick: PropTypes.func,
	updateFloatingPositionReference: PropTypes.func,
	lastSearches: PropTypes.array,
	onFocus: PropTypes.func,
	isCompact: PropTypes.bool,
};

export default LastSearch;
